import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Form, Row, Col, Spinner, ProgressBar } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import { FaCamera, FaPencilAlt, FaSignOutAlt, FaTrash, FaHome, FaExclamationCircle } from 'react-icons/fa';
import {
  getCurrentUser,
  updateUser,
  uploadProfileImage,
  getUserAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  fetchPincodeIndia,
  getFullImageUrl
} from '../api';
import Default from "../assets/Aadvi-logo.png";
import styles from "./UserProfile.module.css";
import '../Orders/Checkout.css'; 
const primaryColor = '#82278A';

// User Profile Component
const UserProfile = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // State variables
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingPin, setIsFetchingPin] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Modal visibility state
  const [modalState, setModalState] = useState({
    showAccount: false,
    showAddress: false,
    showLogout: false,
    showConfirmDeleteAddress: false,
    showImage: false
  });

  // Form data state
  const [accountFormData, setAccountFormData] = useState({});
  const [addressFormData, setAddressFormData] = useState({});
  const [addressStep, setAddressStep] = useState('pincode');
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressToDelete, setAddressToDelete] = useState(null);

  // Generate particles for background
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    duration: Math.random() * 10 + 5 + 's',
    delay: Math.random() * 5 + 's',
    left: Math.random() * 100 + '%'
  }));

  const getUserId = (u) => (u ? (u._id || u.id) : null);

  // Load user data on component mount
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        // Fetch current user data
        const me = await getCurrentUser();
        if (me && me.success && me.data) {
          const current = me.data;
          setUser(current);
          // Pre-fill account form data
          setAccountFormData({
            firstName: current.firstName || '',
            lastName: current.lastName || '',
            email: current.email || '',
            phone: current.phone || ''
          });

          const userId = getUserId(current);
          if (userId) {
            try {
              // Fetch user addresses
              const addrRes = await getUserAddresses(userId);
              if (addrRes && addrRes.success) {
                const arr = Array.isArray(addrRes.data) ? addrRes.data : [];
                arr.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
                setAddresses(arr);
              }
            } catch (err) {
              console.warn('Could not load addresses', err.message || err);
            }
          }
        } else {
          navigate('/login');
        }
      } catch (err) {
        console.error(err);
        toast.error('Session expired. Please log in again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [navigate]);

  // Modal open/close handlers
  const openModal = (name) => setModalState(prev => ({ ...prev, [name]: true }));
  const closeModal = (name) => setModalState(prev => ({ ...prev, [name]: false }));

  // Open image modal
  const openImageModal = () => openModal('showImage');

  const handleFormChange = (e, setter) => {
    const { name, value, type, checked } = e.target;
    setter(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Account form change handler
  const handleAddressFormChange = (e) => {
    setAddressFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Profile image upload handler
  const handleImageUpload = async (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setIsUploading(true);
    try {
      // Create FormData and append file
      const formData = new FormData();
      formData.append('profileImage', file);
      
      // Upload profile image
      const res = await uploadProfileImage(formData);
      if (res && res.message) {
        setUser(prev => ({ ...prev, profileImage: res.profileImage }));
        toast.success('Profile image updated');
        if (res.profileImage) {
          try {
            // Update localStorage with new profile image
            const stored = JSON.parse(localStorage.getItem('user')) || {};
            const updated = { ...stored, profileImage: res.profileImage };
            localStorage.setItem('user', JSON.stringify(updated));
            window.dispatchEvent(new CustomEvent('userUpdated', { detail: updated }));
            localStorage.setItem('user_updated_at', String(Date.now()));
          } catch (err) {
            console.warn('Failed to update localStorage profileImage', err);
          }
        }
        // Close image modal
      } else {
        throw new Error(res.message || 'Upload failed');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Image upload failed');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  // Fetch pincode details and auto-fill city/state
  const fetchPincodeAndFill = async (pincode) => {
    if (!pincode || !/^[0-9]{6}$/.test(String(pincode))) {
      toast.error('Enter a valid PIN code (6 digits)');
      return;
    }
    setIsFetchingPin(true);
    try {
      // Fetch pincode details
      const info = await fetchPincodeIndia(pincode);
      setAddressFormData(prev => ({ ...prev, city: info.district || prev.city, state: info.state || prev.state, country: 'India' }));
      // Advance to details step
      setAddressStep('details');
      toast.success('Auto-filled city/state from PIN');
    } catch (err) {
      toast.error(err.message || 'Could not fetch PIN details');
    } finally {
      setIsFetchingPin(false);
    }
  };
  // Save account changes handler
  const handleSaveAccount = async () => {
    if (!user) return;
    // Validate email format
    setIsSaving(true);
    try {
      // Update user account details
      const res = await updateUser(getUserId(user), accountFormData);
      if (res && res.success) {
        toast.success(res.message || 'Profile updated');
        const me = await getCurrentUser();
        if (me && me.success && me.data) {
            setUser(me.data);
            // Update localStorage with new user data
            const addrRes = await getUserAddresses(getUserId(me.data));
            if (addrRes && addrRes.success) {
              const arr = Array.isArray(addrRes.data) ? addrRes.data : [];
              arr.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
              const primary = arr[0];
              try {
                // Update localStorage user data
                const storedUser = { ...me.data, primaryAddress: primary ? formatAddress(primary) : (me.data.primaryAddress || ''), phone: me.data.phone || (primary ? primary.phone : '') };
                localStorage.setItem('user', JSON.stringify(storedUser));
                window.dispatchEvent(new CustomEvent('userUpdated', { detail: storedUser }));
                localStorage.setItem('user_updated_at', String(Date.now()));
              } catch (err) {
                console.warn('Could not update localStorage', err);
              }
            }
        }
        closeModal('showAccount');
      } else throw new Error(res.message || 'Update failed');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Open address modal handler
  const openAddressModalHandler = (address = null) => {
    if (!user) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
    // If address is null, it's for adding new address
    if (!address) {
      setAddressFormData({ addressLine1: '', city: '', state: '', pincode: '', country: 'India', phone: '', is_default: false });
      setSelectedAddressId(null);
      setAddressStep('pincode');
    } else {
      setAddressFormData({
        // Populate form with existing address data
        _id: address._id || address.id,
        addressLine1: address.street || address.addressLine1 || '',
        city: address.city || '',
        state: address.state || '',
        pincode: address.pincode || '',
        country: address.country || 'India',
        phone: address.phone || '',
        is_default: !!address.is_default
      });
      // Set selected address ID for editing
      setSelectedAddressId(address._id || address.id);
      setAddressStep('details');
    }
    openModal('showAddress');
  };

  // Refresh addresses from server
  const refreshAddresses = async () => {
    if (!user) return;
    try {
      // Fetch user addresses
      const res = await getUserAddresses(getUserId(user));
      if (res && res.success) {
        const arr = Array.isArray(res.data) ? res.data : [];
        arr.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
        setAddresses(arr);
        return arr;
      }
    } catch (err) {
      console.warn('Could not refresh addresses', err.message || err);
    }
    return [];
  };
  // Save address handler
  const handleSaveAddress = async (e) => {
    // Save address handler
    if (e) e.preventDefault();
    if (!user) return;
    setIsProcessing(true);
    try {
      // Validation
      const validate = (data) => {
        if (!data.addressLine1 || !/\d/.test(data.addressLine1)) return 'Address Line 1 must include house/building number';
        if (!data.pincode || !/^[0-9]{6}$/.test(String(data.pincode))) return 'Pincode must be 6 digits';
        const containsNumber = s => /\d/.test(String(s || ''));
        if (containsNumber(data.city)) return 'City must not contain numbers';
        if (containsNumber(data.state)) return 'State must not contain numbers';
        if (containsNumber(data.country)) return 'Country must not contain numbers';
        return null;
      };
      // Perform validation
      const validationError = validate(addressFormData);
      if (validationError) {
        toast.error(validationError);
        setIsProcessing(false);
        return;
      }
      // Prepare payload
      const payload = {
        // Prepare address payload
        user_id: getUserId(user),
        street: addressFormData.addressLine1,
        city: addressFormData.city,
        state: addressFormData.state,
        pincode: addressFormData.pincode,
        country: addressFormData.country,
        phone: addressFormData.phone,
        is_default: !!addressFormData.is_default
      };
      // Create or update address 
      let res;
      if (addressFormData._id) {
        // Update existing address
        res = await updateAddress(addressFormData._id, payload);
      } else {
        res = await createAddress(payload);
      }
      // Handle response
      if (res && res.success) {
        toast.success(res.message || 'Address saved');
        const updated = await refreshAddresses();
        try {
          // Update localStorage with new primary address
          const stored = JSON.parse(localStorage.getItem('user')) || {};
          const primary = updated && updated.length ? updated[0] : null;
          const newStored = { ...stored, primaryAddress: primary ? formatAddress(primary) : (stored.primaryAddress || ''), phone: stored.phone || (primary ? primary.phone : '') };
          localStorage.setItem('user', JSON.stringify(newStored));
          // Dispatch user updated event
          window.dispatchEvent(new CustomEvent('userUpdated', { detail: newStored }));
          localStorage.setItem('user_updated_at', String(Date.now()));
        } catch (err) {
          console.warn('Failed to update localStorage after address save', err);
        }
        closeModal('showAddress');
      } else throw new Error(res.message || 'Address save failed');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to save address');
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete address handler
  const handleDeleteAddress = async (id) => {
    if (!id) return;
    setIsSaving(true);
    try {
      // Delete address
      const res = await deleteAddress(id);
      if (res && res.success) {
        toast.success(res.message || 'Address deleted');
        const updated = await refreshAddresses();
        try {
          // Update localStorage after address deletion
          const stored = JSON.parse(localStorage.getItem('user')) || {};
          const primary = updated && updated.length ? updated[0] : null;
          // Update localStorage with new primary address
          const newStored = { ...stored, primaryAddress: primary ? formatAddress(primary) : (stored.primaryAddress || ''), phone: stored.phone || (primary ? primary.phone : '') };
          localStorage.setItem('user', JSON.stringify(newStored));
          // Dispatch user updated event
          window.dispatchEvent(new CustomEvent('userUpdated', { detail: newStored }));
          localStorage.setItem('user_updated_at', String(Date.now()));
        } catch (err) {
          console.warn('Could not update localStorage after address delete', err);
        }
        closeModal('showConfirmDeleteAddress');
      } else throw new Error(res.message || 'Delete failed');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to delete address');
    } finally {
      setIsSaving(false);
    }
  };
  // Set default address handler
  const handleSetDefaultAddress = async (id) => {
    if (!id || !user) return;
    setIsSaving(true);
    try {
      // Set default address
      const address = addresses.find(a => (a._id || a.id) === id);
      if (!address) throw new Error('Address not found');
      const res = await updateAddress(id, { ...address, user_id: getUserId(user), street: address.street || address.addressLine1, is_default: true });
      // Handle response
      if (res && res.success) {
        toast.success(res.message || 'Default address updated');
        const updated = await refreshAddresses();
        try {
          // Update localStorage with new default address
          const stored = JSON.parse(localStorage.getItem('user')) || {};
          const primary = updated && updated.length ? updated[0] : null;
          const newStored = { ...stored, primaryAddress: primary ? formatAddress(primary) : (stored.primaryAddress || ''), phone: stored.phone || (primary ? primary.phone : '') };
          localStorage.setItem('user', JSON.stringify(newStored));
          // Dispatch user updated event
          window.dispatchEvent(new CustomEvent('userUpdated', { detail: newStored }));
          localStorage.setItem('user_updated_at', String(Date.now()));
        } catch (err) {
          console.warn('Could not update localStorage after setting default', err);
        }
      } else throw new Error(res.message || 'Failed to set default');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to set default address');
    } finally {
      setIsSaving(false);
    }
  };
  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Dispatch user logged out event
    window.dispatchEvent(new Event('storage'));
    window.dispatchEvent(new CustomEvent('userLoggedOut'));
    toast.success('Logged out successfully');
    closeModal('showLogout');
    navigate('/login');
  };
  // Format address for display
  const formatAddress = (addr) => {
    if (!addr) return '';
    const parts = [
      addr.street || addr.addressLine1,
      addr.city,
      addr.state,
      addr.pincode,
      addr.country
    ].filter(Boolean);
    return parts.join(', ');
  };
  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.profileContainer}>
        <div className="text-center py-5 w-100">
          <div className="text-center">
            <Spinner animation="border" className="loading-spinner text-light" style={{ width: '3rem', height: '3rem' }} />
            <div className="mt-3">
              <p className="text-light mb-2">Loading profile...</p>
              <ProgressBar 
                now={100} 
                animated 
                style={{ height: '4px', width: '200px', margin: '0 auto', backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                <ProgressBar 
                  now={100} 
                  animated 
                  style={{ backgroundColor: '#fff' }}
                />
              </ProgressBar>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Render user profile
  if (!user) return null;
  // Determine user image source
  const userImageSrc = user.profileImage ? getFullImageUrl(user.profileImage) : Default;

  return (
    <div className={styles.profileContainer}>
      {/* Background particles */}
      <div className={styles.particles}>
        {particles.map(p => (
          <div
            key={p.id}
            className={styles.particle}
            style={{
              animationDuration: p.duration,
              animationDelay: p.delay,
              left: p.left
            }}
          />
        ))}
      </div>

      <div className="container py-5 position-relative">
        <div className="row g-4">
          <div className="col-md-4">
            <div className={`card p-4 text-center ${styles.profileCard}`}>
              <div className="position-relative d-inline-block mx-auto">
                <div 
                  className={styles.avatarContainer} 
                  onClick={openImageModal}
                  style={{ cursor: 'pointer', position: 'relative', width: 150, height: 150, borderRadius: '50%', overflow: 'hidden', margin: '0 auto', border: `4px solid ${primaryColor}` }}
                >
                  <img 
                    src={userImageSrc} 
                    alt="User" 
                    className={styles.avatarImage} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  {isUploading && (
                    <div className={styles.uploadingOverlay} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                      <div className="spinner-border" role="status"><span className="visually-hidden">Uploading...</span></div>
                    </div>
                  )}
                </div>
                <div 
                  className={styles.cameraIconContainer} 
                  onClick={() => fileInputRef.current?.click()}
                  style={{ position: 'absolute', bottom: 5, right: 'calc(50% - 65px)', background: primaryColor, borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '3px solid white' }}
                >
                  <FaCamera color="white" size={18} />
                </div>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  style={{ display: 'none' }} 
                  accept="image/*" 
                  onChange={handleImageUpload}
                />
              </div>
              <h5 className="mt-3 mb-0">{user.firstName} {user.lastName}</h5>
              <div className="text-muted small" style={{ marginTop: 6 }}>
                {user.email && <div>{user.email}</div>}
                {user.phone && <div>{user.phone}</div>}
                {!user.email && !user.phone && <div>{user.company || user.location}</div>}
              </div>
              <div className="d-grid gap-2 mt-3">
                <Button onClick={() => navigate('/orderhistory')} className={styles.profileBtn}>Order History</Button>
                <Button onClick={() => openModal('showAccount')} className={styles.profileBtn}>
                  <FaPencilAlt className="me-2"/> Edit Profile
                </Button>
                <Button onClick={() => openModal('showLogout')} className={styles.logoutBtn}>
                  <FaSignOutAlt className="me-2"/> Logout
                </Button>
              </div>
            </div>
          </div>

          <div className="col-md-8">
            <div className={`card p-3 ${styles.profileCard}`}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Addresses</h5>
                <div>
                  <Button onClick={() => openAddressModalHandler()} className={styles.addAddressBtn}>
                    <FaHome /> Add Address
                  </Button>
                </div>
              </div>
              <div>
                {addresses.length === 0 ? (
                  <div className="text-center py-4 text-muted">No addresses found. Add one to continue.</div>
                ) : (
                  addresses.map(addr => (
                    <div key={addr._id || addr.id} className={`d-flex align-items-center justify-content-between border rounded p-2 mb-2 ${styles.addressCard}`}>
                      <div>
                        <div className="fw-bold">{formatAddress(addr)}</div>
                        <div className="small text-muted">{addr.phone}</div>
                      </div>
                      <div className="d-flex align-items-center gap-2 address-btns flex-column flex-md-row w-100 w-md-auto" style={{maxWidth:'30%'}}>
                        <Button size="sm" onClick={() => openAddressModalHandler(addr)} className={`${styles.editBtn} w-100 w-md-auto`}>
                          Edit
                        </Button>
                        <Button size="sm" onClick={() => { setAddressToDelete(addr._id || addr.id); openModal('showConfirmDeleteAddress'); }} className={`${styles.deleteBtn} w-100 w-md-auto`}>
                          Delete
                        </Button>
                        {!addr.is_default && (
                          <Button size="sm" onClick={() => handleSetDefaultAddress(addr._id || addr.id)} className={`${styles.defaultBtn} w-100 w-md-auto`}>
                            Set Default
                          </Button>
                        )}
                        {addr.is_default && (
                          <span className={styles.defaultBadge}>Default</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Edit Modal */}
      <Modal 
        show={modalState.showAccount} 
        onHide={() => closeModal('showAccount')} 
        size="lg" 
        centered
        className={styles.boutiqueModal}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title>Edit Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <Form>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Label className={styles.modalLabel}>First Name</Form.Label>
                <Form.Control 
                  name="firstName" 
                  value={accountFormData.firstName || ''} 
                  onChange={(e) => handleFormChange(e, setAccountFormData)}
                  className={styles.modalInput}
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label className={styles.modalLabel}>Last Name</Form.Label>
                <Form.Control 
                  name="lastName" 
                  value={accountFormData.lastName || ''} 
                  onChange={(e) => handleFormChange(e, setAccountFormData)}
                  className={styles.modalInput}
                />
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label className={styles.modalLabel}>Email</Form.Label>
              <Form.Control 
                type="email" 
                name="email" 
                value={accountFormData.email || ''} 
                onChange={(e) => handleFormChange(e, setAccountFormData)}
                className={styles.modalInput}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className={styles.modalLabel}>Phone</Form.Label>
              <Form.Control 
                name="phone" 
                value={accountFormData.phone || ''} 
                onChange={(e) => handleFormChange(e, setAccountFormData)}
                className={styles.modalInput}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button 
            variant="secondary" 
            onClick={() => closeModal('showAccount')} 
            className={styles.cancelBtn}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveAccount} 
            className={styles.saveBtn}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className={styles.spinner}></span>
                Saving...
              </>
            ) : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Address Modal*/}
      <Modal 
        show={modalState.showAddress} 
        onHide={() => closeModal('showAddress')} 
        size="lg" 
        centered
        className={`${styles.boutiqueModal} checkout-address-modal`}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title>{selectedAddressId ? 'Edit Address' : 'Add Address'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody} style={{ padding: 0 }}>
          <div className="address-form-container" style={{ background: 'transparent' }}>
            {addressStep === 'pincode' ? (
              <div className="pincode-step">
                <h3>Enter Pincode</h3>
                <p className="step-description">Enter your pincode to auto-fill address details</p>
                <div className="pincode-input-group">
                  <input 
                    type="text" 
                    className="form-input"
                    name="pincode" 
                    value={addressFormData.pincode || ''} 
                    onChange={handleAddressFormChange}
                    placeholder="Enter 6-digit pincode"
                    maxLength={6}
                  />
                </div>
                <div className="pincode-step-actions">
                  <button 
                    className="btn-primary"
                    onClick={() => fetchPincodeAndFill(addressFormData.pincode)}
                    disabled={isFetchingPin}
                  >
                    {isFetchingPin ? 'Fetching...' : 'Continue'}
                  </button>
                  <button 
                    type="button"
                    className="btn-secondary"
                    onClick={() => setAddressStep('details')}
                  >
                    Enter Manually
                  </button>
                </div>
              </div>
            ) : (
              <div className="address-form-step">
                <h3>{addressFormData._id ? 'Edit Address' : 'Add New Address'}</h3>
                <form onSubmit={handleSaveAddress}>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label>Address Line 1 *</label>
                      <input 
                        type="text" 
                        className="form-input"
                        name="addressLine1" 
                        value={addressFormData.addressLine1 || ''} 
                        onChange={handleAddressFormChange}
                        required
                        placeholder="House/Flat number, Street"
                        autoComplete="address-line1"
                      />
                    </div>
                    <div className="form-group">
                      <label>City *</label>
                      <input 
                        type="text" 
                        className="form-input"
                        name="city" 
                        value={addressFormData.city || ''} 
                        onChange={handleAddressFormChange}
                        required
                        placeholder="City"
                        autoComplete="address-level2"
                      />
                    </div>
                    <div className="form-group">
                      <label>State *</label>
                      <input 
                        type="text" 
                        className="form-input"
                        name="state" 
                        value={addressFormData.state || ''} 
                        onChange={handleAddressFormChange}
                        required
                        placeholder="State"
                        autoComplete="address-level1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Pincode *</label>
                      <input 
                        type="text" 
                        className="form-input"
                        name="pincode" 
                        value={addressFormData.pincode || ''} 
                        onChange={handleAddressFormChange}
                        required
                        placeholder="6-digit Pincode"
                        inputMode="numeric"
                        maxLength={6}
                        autoComplete="postal-code"
                      />
                    </div>
                    <div className="form-group">
                      <label>Country *</label>
                      <input 
                        type="text" 
                        className="form-input"
                        name="country" 
                        value={addressFormData.country || 'India'} 
                        onChange={handleAddressFormChange}
                        required
                        placeholder="Country"
                        autoComplete="country-name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone Number *</label>
                      <input 
                        type="tel" 
                        className="form-input"
                        name="phone" 
                        value={addressFormData.phone || ''} 
                        onChange={handleAddressFormChange}
                        required
                        placeholder="10-digit mobile number"
                        inputMode="tel"
                        maxLength={10}
                        autoComplete="tel"
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button 
                      type="submit"
                      className="btn-primary save-btn"
                      disabled={isProcessing}
                    >
                      {isProcessing ? 'Saving...' : 'Save Address'}
                    </button>
                    <button 
                      type="button"
                      className="btn-secondary cancel-btn"
                      onClick={() => closeModal('showAddress')}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* Logout Confirmation */}
      <Modal 
        show={modalState.showLogout} 
        onHide={() => closeModal('showLogout')} 
        centered
        className={styles.boutiqueModal}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title>Confirm Logout</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className="text-center">
            <FaExclamationCircle size={48} style={{ color: primaryColor }} />
            <p className="mt-3">Are you sure you want to logout?</p>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button 
            variant="secondary" 
            onClick={() => closeModal('showLogout')} 
            className={styles.cancelBtn}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleLogout} 
            className={styles.saveBtn}
          >
            Logout
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Address Confirmation */}
      <Modal 
        show={modalState.showConfirmDeleteAddress} 
        onHide={() => closeModal('showConfirmDeleteAddress')} 
        centered
        className={styles.boutiqueModal}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className="text-center">
            <FaTrash size={48} style={{ color: '#dc3545' }} />
            <p className="mt-3">Are you sure you want to delete this address?</p>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button 
            variant="secondary" 
            onClick={() => closeModal('showConfirmDeleteAddress')} 
            className={styles.cancelBtn}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleDeleteAddress(addressToDelete)} 
            className={styles.deleteBtn}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className={styles.spinner}></span>
                Deleting...
              </>
            ) : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Preview Modal */}
      <Modal 
        show={modalState.showImage} 
        onHide={() => closeModal('showImage')} 
        centered
        size="lg"
        className={styles.imageModal}
      >
        <Modal.Header closeButton style={{ border: 'none' }} />
        <Modal.Body className="text-center p-0">
          <img 
            src={userImageSrc} 
            alt="Profile" 
            style={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default UserProfile;
